// Where the time went: waits and stalls grouped by who waited, on what, and who
// released them, with totals, the longest one to jump to, and a switch between the
// whole run and the current window. Waits for the host are hidden by default -- they
// are the host thinking, not the program -- and a toggle shows them.

import { el, fmtPs, fmtInt } from "./dom.js";
import { coreShort } from "./fmt.js";
import { rowKeyOf } from "./links.js";

// Who released a wait, as the list names it and as it groups by. The name carries the
// DMA that released this one; the key carries only what issued it, so the descriptors
// of one queue are one group.
const byOf = (r) => (r.how === "dma" ? `DMA #${r.dma.id} from ${r.dma.byKind === "host" ? "the host" : coreShort(r.dma.byKind === "tc" ? "TensorCore" : "SparseCore", r.dma.byCi)}`
  : r.how === "core" ? r.core.label : r.how === "host" ? "the host" : r.how);
const byKeyOf = (r) => (r.how === "dma" ? `dma:${r.dma.byKind}:${r.dma.chip}:${r.dma.byCi}` : r.how === "core" ? "core:" + r.core.key : r.how);

export class Stalls {
  constructor(root, ctx, opts = {}) {
    this.ctx = ctx;
    this.opts = { limit: 40, ...opts };
    this.root = root;
    this.filter = "";
    this.showHost = false;
    this.inWindow = false;
    this.window = null;
    this.build();
  }

  build() {
    const { model, links } = this.ctx;
    this.root.classList.add("stalls");
    const head = el("div", "stalls-head");
    head.appendChild(el("span", "strong", "stalls"));
    const inp = el("input");
    inp.placeholder = "filter: chip 2, SC1, flag 507, barrier…";
    inp.oninput = () => { this.filter = inp.value.trim().toLowerCase(); this.fill(); };
    head.appendChild(inp);
    const host = el("label", "dim");
    const hc = el("input"); hc.type = "checkbox"; hc.checked = this.showHost;
    hc.onchange = () => { this.showHost = hc.checked; this.fill(); };
    host.appendChild(hc); host.appendChild(document.createTextNode(" host waits"));
    host.title = "waits released by the host or by nothing in the trace: the core idle while the host thinks";
    head.appendChild(host);
    const win = el("label", "dim");
    const wc = el("input"); wc.type = "checkbox"; wc.checked = this.inWindow;
    wc.onchange = () => { this.inWindow = wc.checked; this.fill(); };
    win.appendChild(wc); win.appendChild(document.createTextNode(" in window"));
    win.title = "only what overlaps the timeline's current window";
    head.appendChild(win);
    this.count = el("span", "dim");
    head.appendChild(this.count);
    this.root.appendChild(head);
    this.table = el("table", "grid");
    this.root.appendChild(this.table);

    // Every wait and stall is a row of two arrays rather than an object of its own: a
    // slice parks a quarter of a million times, the list shows groups of them forty at
    // a time, and an item is built for the rows it draws (`item`). `ids` says what a
    // row is -- a stall is -1 - i, a wait is i -- and `gid` which group it falls in,
    // numbered as the groups are met. A SparseCore tile's waits are attributed on
    // demand (links.js) and there are hundreds of thousands of them on a slice: the
    // list leaves those rows out until the timeline says it is showing them.
    this.shownRows = new Set();
    this.n = 0;
    this.ids = new Int32Array(1024);
    this.gid = new Int32Array(1024);
    this.groupOf = new Map();                // group key -> its number
    this.heads = [];                         // group -> what it fixes of the filter's text
    for (const s of model.stalls) this.add(-1 - s.i, `${s.core.key}|${s.reason}`, `${s.core.key} ${s.core.label} ${s.reason}`);
    for (const k of links.waitsByRow.keys()) if (!links.deferredRows.has(k)) this.addRow(k);
    this.fill();
  }

  add(id, key, head) {
    let g = this.groupOf.get(key);
    if (g === undefined) { this.groupOf.set(key, g = this.heads.length); this.heads.push((head + " ").toLowerCase()); }
    if (this.n === this.ids.length) {
      const grow = (a) => { const b = new Int32Array(a.length * 2); b.set(a); return b; };
      this.ids = grow(this.ids); this.gid = grow(this.gid);
    }
    this.ids[this.n] = id;
    this.gid[this.n++] = g;
  }

  // The item a drawn row stands for: the two of each group the table shows, and the one
  // a click or the s key hands on.
  item(id) {
    const { model, links } = this.ctx;
    if (id < 0) {
      const s = model.stalls[-1 - id];
      return { w: s, r: { how: s.reason, text: s.reason }, row: s.core.key, who: s.core.label, what: s.reason, by: "-", cls: "core", key: `${s.core.key}|${s.reason}` };
    }
    const w = model.waits[id], r = links.releaserOf(w);
    return { w, r, row: rowKeyOf(w.chip, w.core, w.ci, w.tile), who: `chip ${w.chip} ${coreShort(w.core, w.ci, w.tile)}`, what: `flag ${w.idx}`, by: byOf(r), cls: w.cls, key: `${w.chip}|${w.core}|${w.ci}|${w.tile}|flag ${w.idx}|${byKeyOf(r)}` };
  }

  addRow(key) {
    if (this.shownRows.has(key)) return false;
    this.shownRows.add(key);
    const { model, links } = this.ctx;
    links.resolveRow(key);
    for (const i of links.waitsByRow.get(key) || []) {
      const w = model.waits[i], r = links.releaserOf(w);
      const row = rowKeyOf(w.chip, w.core, w.ci, w.tile), who = `chip ${w.chip} ${coreShort(w.core, w.ci, w.tile)}`, what = `flag ${w.idx}`;
      this.add(i, `${w.chip}|${w.core}|${w.ci}|${w.tile}|${what}|${byKeyOf(r)}`, `${row} ${who} ${what}`);
    }
    return true;
  }

  // The filter matches what a row shows and what released the item, and the releaser's
  // text is the item's own -- the writes of one group name different pcs. So the part
  // the group fixes is tried first, and the record built only where that fails.
  matches(id, g) {
    if (this.heads[g].includes(this.filter)) return true;
    const it = this.item(id);
    return `${it.row} ${it.who} ${it.what} ${it.by} ${it.r.how} ${it.r.text}`.toLowerCase().includes(this.filter);
  }

  // The rows the timeline is showing that the list left out: their waits join it.
  setTileRows(keys) {
    let added = false;
    for (const k of keys) added = this.addRow(k) || added;
    if (added) this.fill();
  }

  setWindow(t0, t1) {
    this.window = [t0, t1];
    if (this.inWindow) this.fill();
  }

  // The groups the table shows, counted out over the arrays: a group's item objects --
  // the first one that survived the filters, which is what it shows itself as, and the
  // longest, which is what a click on it jumps to -- are built at the end, for the
  // groups that have any.
  groups() {
    const m = this.ctx.model;
    const [w0, w1] = this.window || [-Infinity, Infinity];
    const G = this.heads.length, NONE = 0x7fffffff;
    const n = new Int32Array(G), open = new Int32Array(G), total = new Float64Array(G);
    const first = new Int32Array(G).fill(NONE), longest = new Int32Array(G).fill(NONE);
    const lStall = new Float64Array(G), lOpen = new Uint8Array(G);
    const order = [];                        // the groups that have anything, as met
    for (let k = 0; k < this.n; k++) {
      const id = this.ids[k], w = id < 0 ? m.stalls[-1 - id] : m.waits[id];
      if (!this.showHost && w.cls === "host") continue;
      if (this.inWindow) { const t1 = w.open ? m.tEnd : w.t1; if (t1 < w0 || w.t0 > w1) continue; }
      const g = this.gid[k];
      if (this.filter && !this.matches(id, g)) continue;
      if (first[g] === NONE) { first[g] = id; order.push(g); }
      n[g]++; total[g] += w.stallPs;
      if (w.open) open[g]++;
      if (longest[g] === NONE || w.stallPs > lStall[g] || (w.open && !lOpen[g])) { longest[g] = id; lStall[g] = w.stallPs; lOpen[g] = w.open ? 1 : 0; }
    }
    // In the order they were met, which is what two groups that tie are left in.
    const out = order.map((g) => ({ ...this.item(first[g]), n: n[g], total: total[g], longest: this.item(longest[g]), open: open[g] }));
    return out.sort((a, b) => (b.open - a.open) || (b.total - a.total));
  }

  fill() {
    const t = this.table;
    t.textContent = "";
    const h = el("tr");
    for (const c of ["total", "count", "longest", "who", "on", "released by"]) h.appendChild(el("th", null, c));
    t.appendChild(h);
    const groups = this.groups();
    let shown = 0;
    for (const g of groups) {
      if (shown >= this.opts.limit) break;
      shown++;
      const tr = el("tr", "click" + (this.selected === g.longest.w ? " on" : ""));
      tr.appendChild(el("td", null, g.open ? `${fmtPs(g.total)} + ${g.open} open` : fmtPs(g.total)));
      tr.appendChild(el("td", null, fmtInt(g.n)));
      tr.appendChild(el("td", null, g.longest.w.open ? "open" : fmtPs(g.longest.w.stallPs)));
      tr.appendChild(el("td", null, g.who));
      tr.appendChild(el("td", null, g.what));
      tr.appendChild(el("td", null, g.by));
      tr.title = "click: jump to the longest one";
      tr.onclick = () => { const it = g.longest; this.selected = it.w; if (this.opts.onPick) this.opts.onPick(it.w, it.r, it.row); this.fill(); };
      t.appendChild(tr);
    }
    const total = groups.reduce((a, g) => a + g.n, 0);
    this.count.textContent = `${shown} groups of ${fmtInt(total)} stalls`;
  }

  select(w) { this.selected = w; this.fill(); }

  // The worst items in order, for cycling with a key.
  get rows() {
    return this.groups().map((g) => g.longest);
  }
}
